import { computed, DestroyRef, inject, Injectable, linkedSignal, signal } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { finalize } from 'rxjs'
import { NotificationQuery } from '@awork/framework/state/notification.query'
import { NotificationService } from '@awork/framework/services/notification-service/notification.service'
import type { Notification } from '@awork/framework/models/notification.model'
import { TaskQuery } from '@awork/features/task/state/task.query'
import type { Task } from '@awork/features/task/models/task.model'
import { UserQuery } from '@awork/features/user/state/user.query'
import { areArraysEqual } from '@awork/core/state/signal-store/helpers'
import { AppService } from '../../../../app.service'
import { ViewPersistentService } from '../../../../shared/services/view-persistent/view-persistent.service'
import { InlineListSearchService } from '../../../../shared/services/inline-list-search/inline-list-search.service'
import { ToastService } from '../../../../shared/services/toast-service/toast.service'
import type { NotificationView } from './utils'
import {
  getNotificationSections,
  getUnreadCounts,
  getVisibleNotifications,
  isUnread,
  mapListNotifications,
  mergeNotifications
} from './utils'

const SEEN_CONVERSATIONS_PAGE_SIZE = 20

/** Owns query-backed notification data, view selection and paged history for one mounted view. */
@Injectable()
export class NotificationViewFacade {
  private readonly notificationService = inject(NotificationService)
  private readonly notificationQuery = inject(NotificationQuery)
  private readonly taskQuery = inject(TaskQuery)
  private readonly persistence = inject(ViewPersistentService)
  private readonly inlineSearch = inject(InlineListSearchService)
  private readonly toast = inject(ToastService)
  private readonly destroyRef = inject(DestroyRef)
  private readonly userQuery = inject(UserQuery)
  private readonly appService = inject(AppService)
  private readonly notifications = this.notificationQuery.queryAll()
  private readonly seenConversations = signal<Notification[]>([])
  private readonly page = signal(0)
  private readonly totalPages = signal(1)
  private readonly historyLoading = signal(false)
  private readonly historyFailed = signal(false)

  readonly sheet = signal(false)
  readonly activeView = signal<NotificationView>(
    this.persistence.getActiveView('notifications:view') === 'notifications' ? 'notifications' : 'inbox'
  )
  readonly showSeenMessages = signal(this.persistence.getActiveView('notifications:show-seen') === 'true')
  readonly searchTerm = signal(this.inlineSearch.get('notifications'))
  private readonly listStateKey = computed(() =>
    this.sheet() ? 'notifications:sheet' : `notifications:${this.activeView()}`
  )
  readonly expandedConversations = linkedSignal<ReadonlySet<string>>(
    () => new Set(this.persistence.getListExpansion(`${this.listStateKey()}:expanded`))
  )
  readonly collapsedSections = linkedSignal<ReadonlySet<string>>(
    () => new Set(this.persistence.getListExpansion(`${this.listStateKey()}:collapsed`))
  )
  readonly markingSections = signal<ReadonlySet<string>>(new Set())
  readonly pendingNotifications = signal<ReadonlySet<string>>(new Set())
  readonly markingAll = signal(false)
  readonly currentUser = this.userQuery.queryCurrentUser()
  readonly onlineStatus = this.appService.onlineStatus
  readonly seenConversationPage = this.page.asReadonly()
  readonly loadingSeenConversations = this.historyLoading.asReadonly()
  readonly failedSeenConversations = this.historyFailed.asReadonly()
  readonly hasMoreSeenConversations = computed(() => this.page() < this.totalPages())
  private readonly mergedNotifications = computed(() =>
    mergeNotifications(
      this.notifications(),
      !this.sheet() && this.activeView() === 'inbox' && this.showSeenMessages() ? this.seenConversations() : []
    )
  )
  private readonly taskIds = computed(
    () =>
      this.sheet()
        ? []
        : Array.from(
            new Set(
              this.mergedNotifications()
                .filter(notification => notification.isConversation)
                .map(notification => notification.taskId || notification.task?.id)
                .filter((id): id is string => !!id)
            )
          ),
    { equal: areArraysEqual }
  )
  private readonly taskSelection = computed(() => this.taskQuery.queryMany(this.taskIds()))
  private readonly tasks = computed(() => this.getTasks())
  readonly listNotifications = computed(() =>
    mapListNotifications(this.mergedNotifications(), this.tasks(), this.currentUser())
  )
  readonly visibleNotifications = computed(() =>
    getVisibleNotifications(this.listNotifications(), {
      sheet: this.sheet(),
      view: this.activeView(),
      showSeen: this.showSeenMessages(),
      search: this.searchTerm()
    })
  )
  readonly sections = computed(() =>
    getNotificationSections(this.visibleNotifications(), {
      inbox: !this.sheet() && this.activeView() === 'inbox',
      search: this.searchTerm(),
      expanded: this.expandedConversations(),
      pending: this.pendingNotifications(),
      translations: q.translations.Notifications
    })
  )
  readonly unseenVisibleNotifications = computed(() => this.visibleNotifications().filter(isUnread))
  readonly unreadByView = computed(() => getUnreadCounts(this.listNotifications()))
  readonly canMarkAllAsSeen = computed(
    () =>
      this.onlineStatus() &&
      this.unseenVisibleNotifications().length > 0 &&
      !this.markingAll() &&
      this.markingSections().size === 0 &&
      this.pendingNotifications().size === 0
  )
  readonly isLoading = this.notificationQuery.queryLoading
  readonly showLoading = computed(() => this.isLoading() && this.listNotifications().length === 0)
  readonly unreadCount = this.notificationQuery.querytNewNotificationsCount()

  /** Saves section and conversation state for the current notification view. */
  saveListState(): void {
    this.persistence.updateListExpansion(`${this.listStateKey()}:expanded`, [...this.expandedConversations()])
    this.persistence.updateListExpansion(`${this.listStateKey()}:collapsed`, [...this.collapsedSections()])
  }

  /**
   * Reads the stable query for the conversation task IDs.
   * @withSignal {Signal<Signal<Task[]>>} taskSelection
   * @returns {Task[]} Current tasks for the selected notifications.
   */
  private getTasks(): Task[] {
    const selectedTasks = this.taskSelection()

    return selectedTasks()
  }

  /** Fetches read conversations without dropping retained messages after a failure.
   * @param [reset=false] Starts again at the newest history page.
   */
  loadSeenConversations(reset = false): void {
    if (this.loadingSeenConversations() || !this.onlineStatus()) {
      return
    }

    const page = reset ? 1 : this.page() + 1

    this.historyFailed.set(false)
    this.historyLoading.set(true)
    this.notificationService
      .fetchSeenConversations(page, SEEN_CONVERSATIONS_PAGE_SIZE)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.historyLoading.set(false))
      )
      .subscribe({
        next: ({ conversations, totalPages }) => {
          this.seenConversations.update(current => (reset ? conversations : [...current, ...conversations]))
          this.page.set(page)
          this.totalPages.set(totalPages)
        },
        error: () => {
          this.historyFailed.set(true)
          this.toast.show(q.translations.Notifications.loadSeenFailed, 'warning')
        }
      })
  }
}
